import React from 'react';
import {
    createStyles,
    Theme,
    withStyles,
    WithStyles
} from '@material-ui/core/styles';
import {List, Dialog} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import {getCurrentPolicyYear, nextAnniversaryDate} from "../../../pages/clientServicing/ClientServiceDashbaord";
import { useSelector } from "react-redux";
import { RootState } from "../../../reducers";
import { usePolicyContext } from "./PolicyContext";
import moment from "moment";
import numeral from "numeral";
import {AccountType, ClientPolicyPaymentMethod} from "../../../constants";
import { useActions } from "../../../actions";
import * as PaymentActions from "../../../actions/payment"
import {isDebug, joinAsFullName} from "../../../utils/commonUtils";
import {Button} from "../../formInputs";


const styles = (theme: Theme) =>
        createStyles({
            root: {
                margin: 0,
                padding: theme.spacing(2),
            },
            closeButton: {
                position: 'absolute',
                right: theme.spacing(1),
                top: 18,
                color: theme.palette.grey[500],
                [theme.breakpoints.down("sm")]: {
                    top:0
                },
            },
        });

export interface DialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
            <MuiDialogTitle disableTypography className={classes.root} {...other}>
                <Typography variant="h6">{children}</Typography>
                {onClose ? (
                        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                ) : null}
            </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

export default function WireTransferPaymentModal(props: any) {
    const { onClose, caseDetail } = props;
    const issueDate = caseDetail && caseDetail.tranche && caseDetail.tranche.issueDate;
    let anniversaryDate = nextAnniversaryDate(issueDate);
    let currentAnniversaryYear = getCurrentPolicyYear(anniversaryDate);
    const handleClose = () => {
        onClose();
    };
    const clientDetail = useSelector((state: RootState) => state.clientAccount);
    const profile = useSelector((state: RootState) => state.profile);
    const {user, parentPortal} = useSelector((state: RootState) => state.auth);
    const paymentAction = useActions(PaymentActions);
    const {policy}: any = usePolicyContext();
    const isParentPortal = user.role === AccountType.Individual && parentPortal;
    const isGWTParent = policy && policy.isGWTParent;
    const [isConfirming, setIsConfirming] = React.useState(false);
    isDebug() && console.info({isParentPortal, isGWTParent})

    let clientPolicy: any = profile.client && profile.client.clientPolicies && profile.client.clientPolicies[0] && profile.client.clientPolicies[0];
    if (isParentPortal) {
        clientPolicy = clientDetail.client && clientDetail.client.clientPolicies && clientDetail.client.clientPolicies.length > 0 && clientDetail.client.clientPolicies[0];
    }
    const paymentFrequency: string = clientPolicy && clientPolicy.paymentFrequency || "annual";
    const timeTable = paymentFrequency === "annual" ? policy && policy.paymentTimetableAnnual : policy && policy.paymentTimetableMonthly;
    const nextPaymentDetails = timeTable && timeTable.nextPayment;
    const recurringPaymentDate = nextPaymentDetails && nextPaymentDetails.date ? moment(nextPaymentDetails.date).format("MM/DD/YYYY") : "";
    const recurringPaymentAmount = nextPaymentDetails && nextPaymentDetails.amount ?  numeral(nextPaymentDetails.amount[0].amount + nextPaymentDetails.amount[1].amount).format("$0,0") : <span style={{color: "#3cda93"}}>No payment due</span>;

    const strategyName = clientPolicy && clientPolicy.strategy && clientPolicy.strategy.name || "Kai-Zen";
    const buttonName = isParentPortal ? "Confirm Payment Method" : "Close";
    const referenceText = isParentPortal ? `${caseDetail && caseDetail.tranche && caseDetail.tranche.trancheNumber}` : `
                                    ${caseDetail && caseDetail.user && caseDetail.user.name}, 
                                    ${caseDetail && caseDetail.trustDetails && caseDetail.trustDetails.trustName} /
                                    ${caseDetail && caseDetail.tranche && caseDetail.tranche.trancheNumber}
                                    `;
    const onGen1PaymentConfirm = async () => {
        const gen1ClientPolicyId = user && user.client && user.client.clientPolicies && user.client.clientPolicies.length > 0 && user.client.clientPolicies[0].id;
        setIsConfirming(true);
        const payload = {
            gen1ClientPolicyId,
            gen2ClientPolicyId: clientPolicy && clientPolicy.id,
            paymentMethod: ClientPolicyPaymentMethod.WireTransfer,
        };
        await paymentAction.confirmPaymentForGen1(payload);

        // manual payment
        const paymentPayload = {
            policyNumber: clientPolicy && clientPolicy.id,
            clientPayment: {
                amount: [{amount: numeral(recurringPaymentAmount).value(), type: "Premium"}],
                date: moment().format("YYYY-MM-DD"),
                details: "Wire Transfer for Gen2 GWT Policy",
                processedBy: joinAsFullName(user),
                type: "Wire",
            }
        };
        await paymentAction.createManualPayment(paymentPayload);
        handleClose();
    };

    const onButtonAndCloseClick = isParentPortal ? onGen1PaymentConfirm : handleClose;

    return (
            <Dialog fullWidth={true} maxWidth={"md"} onClose={handleClose} aria-labelledby="customized-dialog-title" open={true}>
                <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                    Wire Transfer Payment Details
                </DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        Please instruct the sending bank to wire the funds through the Federal Reserve as follows. Once
                        payment is received, your contribution totals and payment history will be updated. You might
                        want to write these details down.
                    </Typography>
                    <span className="hLine"/>
                        <List className="textLeft">
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">{currentAnniversaryYear} Contribution Balance Owed :</Typography>
                                <Typography variant="body1" className="floatRight">
                                    {recurringPaymentAmount}
                                </Typography>
                            </li>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Check must be in by:</Typography>
                                <Typography variant="body1" className="floatRight"> {recurringPaymentDate} </Typography>
                            </li>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Receivers Bank Name:</Typography>
                                <Typography variant="body1" className="floatRight">Well Fargo Bank, N.A. 420 Montgomery St. San Francisco, CA 9410 </Typography>
                            </li>
                            <Typography variant= "body1" className="textRed">The address may vary based on client's region. Address is displayed after account and routing number are entered.</Typography>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">ABA/Routing Number:</Typography>
                                <Typography variant="body1" className="floatRight"> 121000248</Typography>

                            </li>

                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Beneficiary Account#:</Typography>
                                <Typography variant="body1" className="floatRight"> 4077370120</Typography>

                            </li>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Beneficiary Name:</Typography>
                                <Typography variant="body1" className="floatRight"> {strategyName}</Typography>

                            </li>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Beneficiary Address:</Typography>
                                <address className="floatRight">
                                    7450 Arroyo Crossing Parkway, Suite 230
                                    Las Vegas, NV 89113
                                </address>
                            </li>
                            <li className="floatLeft w100">
                                <Typography variant="body1" className="floatLeft">Reference:</Typography>
                                <address className="floatRight">
                                    {referenceText}
                                </address>
                            </li>
                    </List>
                    <Typography variant={"body1"}>
                        Funds must originate from a personal account owned by the client and/or settlor of the trust.
                        Funds cannot come from an account associated with a business or trust. <Typography
                            className="textRed" component="span">FUNDS MUST BE SENT AS A WIRE TRANSFER, NOT AN ACH.</Typography>
                    </Typography>
                    <Typography variant={"body1"}>
                        If funds do not originate from a personal account – wire will be returned to the client at the
                        client’s expense. Any exceptions, please contact NIW Companies (Kaizen Plan) before sending
                        wire. 972-755-1582
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                            className="mt20 mb20"
                            color="primary"
                            label={buttonName}
                            loading={isConfirming}
                            onClick={onButtonAndCloseClick}
                            variant="contained"
                    />
                </DialogActions>
            </Dialog>
    );
}